import { useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * The screen height sampled while the keyboard is CLOSED, in px.
 *
 * Aug 20 2026. BottomSheet needs a height basis that does not blink. Both of
 * the obvious candidates do:
 *
 *   --vv-height  Index.tsx releases it on the RAW Median close signal
 *                (:799-823, deliberately un-debounced) so that chat dismiss
 *                feels immediate. Device ev() logs show iOS raw-closes and
 *                reopens the keyboard between EVERY field (F:0 blip, then
 *                T:301), so the var round-trips on every field hop even
 *                though the keyboard never actually goes away.
 *   dvh          does not shrink for the keyboard in WKWebView, and `vv.iH`
 *                was observed flapping 577<->878 across focus changes.
 *
 * `useStableKeyboardOffset` already publishes a hysteresis-protected offset
 * that holds steady through those blips. This hook is the other half of the
 * pair: a full height that is frozen the same way. Together they give the
 * sheet a geometry that cannot move while the keyboard is up.
 *
 * The var keeps its raw-release behaviour untouched — that is chat's contract
 * and this hook does not read, write or observe it.
 *
 * DUPLICATION, on purpose and worth removing later: this mirrors the capture
 * discipline at Index.tsx:752-757 (sample only while closed, keep the MAX so
 * address-bar fluctuation cannot shrink it, reset on orientation change).
 * Index's copy is a private useRef with no export, and hoisting it into a
 * shared hook would mean editing the file that owns the --vv-height lifecycle.
 * That was explicitly out of scope for the fix this was written for. If a
 * third consumer ever appears, extract this and have Index.tsx consume it.
 */
export function useStableFullHeight(keyboardOffset: number): number {
  // Measured synchronously on first render so geometry never has to fall back
  // to a viewport unit. WKWebView reports the full height here even with the
  // keyboard up (device reading: liveH stayed 878 while med.kbH was 301).
  const [fullHeight, setFullHeight] = useState(() =>
    typeof document === "undefined" ? 0 : document.documentElement.clientHeight,
  );
  const ref = useRef(fullHeight);

  useLayoutEffect(() => {
    // Only sample while the keyboard is confirmed closed. Same >80 threshold
    // as Index.tsx:357 and sheetKeyboardGeometry — below that the value is the
    // focus sentinel or the accessory bar, not a keyboard.
    if (keyboardOffset > 80) return;
    const next = Math.max(ref.current, document.documentElement.clientHeight);
    if (next !== ref.current) {
      ref.current = next;
      setFullHeight(next);
    }
  }, [keyboardOffset]);

  useEffect(() => {
    // Re-measure rather than reset-to-zero: a sheet open across a rotation
    // still needs a usable height on the very next frame, and unlike
    // Index.tsx's copy there is no guarantee another keyboard event will
    // arrive to repopulate it. rAF because the new dimensions are not
    // readable synchronously from the event.
    const onOrient = () => {
      requestAnimationFrame(() => {
        const h = document.documentElement.clientHeight;
        ref.current = h;
        setFullHeight(h);
      });
    };
    window.addEventListener("orientationchange", onOrient);
    return () => window.removeEventListener("orientationchange", onOrient);
  }, []);

  return fullHeight;
}
